import "@gnaudio/jabra-browser-integration/jabra.browser.integration-3.0";
import defaultLogger from "./defaultLogger";
import packageInfo from "../package.json";
import StateButtonController from "./StateButtonController";
import amazonConnectAgentStates from "./AmazonConnectAgentState";
import DeviceValidator from "./DeviceValidator";
import "amazon-connect-streams";

async function jabraRCC(logger) {
  logger = logger ?? defaultLogger;
  const stateButtonController = new StateButtonController(logger, null);
  const deviceValidator = new DeviceValidator(logger);

  const callState = {
    onHold: false,
    offHook: false,
    muted: false,
  };

  /** @type {connect.Agent} */
  let activeAmazonAgent = undefined;

  let isJabraDevice = false;

  let jabraHeadsetState = undefined;

  let headsetStates = {
    ONHOOK: "onhook",
    OFFHOOK: "offhook",
    RING: "ring",
    UNRING: "unring"    
  };

  function setIdleState() {
    jabraHeadsetState = headsetStates.ONHOOK;
    callState.offHook = false;
    callState.onHold = false;

    stateButtonController.alignStateButtonWithAgentState(activeAmazonAgent.getState().name);
  }

  function getActiveVoiceContact() {
    try {
      var contacts = activeAmazonAgent.getContacts(connect.ContactType.VOICE);
      if (contacts.length > 0) {
        return contacts[0];
      }
    } catch (error) {
      logger.debug("Could not find active contact", error);
    }

    return null;
  }

  /**
   * Align the active device with the current state.
   */
  async function alignDeviceState() {
    try {
      logger.debug(`Setting up device with current state: ${jabraHeadsetState}`);
      // If there is a active contact in session(when plugging in a device) the state could be offhook/hold/ring/resume and the state button color to original(undefined)
      // check the state and set HS to right state, if undefined(just started the integration) or resumed(waiting for call), nothing should be done
      switch (jabraHeadsetState) {
        case headsetStates.OFFHOOK:
          jabra.ring();
          jabra.offHook();
          break;
        case headsetStates.RING:
          spc.fromSPConnecting();
          break;
        case undefined:
          // Do nothing - is in no state(just started the integration)
          break;
        case headsetStates.ONHOOK:
          jabra.onHook();
          if (callState.onHold) {
            jabra.hold();
          }
          break;
        default:
          logger.debug(`Jabra state was in '${jabraHeadsetState}' and we did not expect that.`);
      }

      if(callState.muted) {
        jabra.mute();
      }

      let state = activeAmazonAgent ? activeAmazonAgent.getState().name : amazonConnectAgentStates.AVAILABLE;
      logger.debug(`Align device with agent state ${state}.`);
      stateButtonController.alignStateButtonWithAgentState(state);
    } catch (e) {
      logger.error("An error occurred when synchronizing device state:", e);
    }
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Setting up the jabra device
   * @param {DeviceInfo} newDeviceAttached - the device that was just attached
   */
  async function setupDevice(newDeviceAttached = null) {
    let getDevicesResult = await jabra.getDevices();
    // no devices attached - does not go further, returns here.
    if (getDevicesResult.length === 0) {
      // Don't set up devices if there isn't any
      logger.info("There is no Jabra device attached.");
      isJabraDevice = false;
      return;
    }

    var jabraActiveDevice;
    // set up new device(when attaching a device)
    if (newDeviceAttached) {
      logger.debug("The active device is set as the newly attached device.");
      jabraActiveDevice = newDeviceAttached;
    }
    // Find the active device
    else {
      logger.debug("The active device is from the getActiveDevice()");
      jabraActiveDevice = await jabra.getActiveDevice();
    }

    logger.debug(
      `The active call control device is '${jabraActiveDevice.deviceName}' with FW ${jabraActiveDevice.firmwareVersion}`
    );

    if (!deviceValidator.isValidDevice(jabraActiveDevice)) {
      return;
    }

    // Set device id so jabra knows which device we are talking to
    jabra.setActiveDeviceId(jabraActiveDevice.deviceID);

    isJabraDevice = true;

    // Avoid interference with JDO startup sequence.
    await sleep(jabraRCC.setupMargin);
    await stateButtonController.setActiveDevice(jabraActiveDevice);
    await alignDeviceState();
  }

  // Jabra library init with full installation check, focus setup and diagnostics of common problems:
  try {
    logger.info(`This is version ${packageInfo.version} of the Jabra Amazon Connect Integration.`);
    await jabra.init();
    logger.debug("Jabra initialized");
    await jabra.getInstallInfo();
    await setupDevice();
  } catch (err) {
    if (err.name === "CommandError" && err.errmessage === "Unknown cmd" && err.command === "getinstallinfo") {
      logger.error("Your browser SDK installation is incomplete, out of date or corrupted. Please (re)install");
    } else if (err.name === "NotFoundError") {
      logger.error("Jabra input device not accessible/found");
    } else {
      if (err.message.includes("Jabra Browser Integration") && err.message.includes("</a>")) {
        let httpIndex = err.message.indexOf("https");
        let startOfLink = err.message.slice(httpIndex);
        let linkEndIndex = startOfLink.indexOf("'>Extension");
        let link = startOfLink.slice(0, linkEndIndex);

        logger.error(
          `The Jabra Browser Integration Chrome Extension must be installed. You can add it from here: ${link}, and then reload this page.`
        );
      } else {
        logger.error(err.name + ": " + err.message);
      }
    }
  }

  class SPCall {
    //when jabra ringing
    fromSPConnecting() {
      jabra.ring();
      jabraHeadsetState = headsetStates.RING;
      logger.debug("Jabra device is ringing now");
    }

    fromSPConnected() {
      jabra.offHook();
      jabraHeadsetState = headsetStates.OFFHOOK;
      logger.debug("Jabra device is 'off hook' now");
    }
  }

  let spc = new SPCall();

  function setHoldState(isOnHold) {
    if (!isJabraDevice || callState.onHold === isOnHold) {
      return;
    }

    if (isOnHold) {
      jabra.hold();
    } else {
      jabra.resume();
    }

    callState.onHold = isOnHold;
    const stateName = isOnHold ? "on hold" : "resumed";
    logger.debug(`Jabra device is '${stateName}' now`);
    stateButtonController.alignStateButtonWithAgentState(activeAmazonAgent.getState().name);
  }

  function setHookState(isOffHook) {
    if (!isJabraDevice || callState.offHook === isOffHook) {
      return;
    }

    if (isOffHook) {
      jabra.offHook();
      jabraHeadsetState = headsetStates.OFFHOOK;
    } else {
      jabraHeadsetState = headsetStates.ONHOOK;
      jabra.onHook();
    }

    callState.offHook = isOffHook;
    const stateName = isOffHook ? "off" : "on";
    logger.debug(`Jabra device is '${stateName} hook' now`);
    stateButtonController.alignStateButtonWithAgentState(activeAmazonAgent.getState().name);
  }

  function setMuteState(isMuted) {
    if (!isJabraDevice || callState.muted === isMuted) {
      return;
    }

    if (isMuted) {
      jabra.mute();      
    } else {
      jabra.unmute();      
    }

    callState.muted = isMuted;
    const stateName = isMuted ? "muted" : "unmuted";
    logger.debug(`Jabra device is '${stateName}' now`);
  }

  /**
   * Checks in the contact is a voice call and not a chat
   * @param {connect.Contact} contact
   */
  function isVoiceCall(contact) {
    return contact.getType() !== "chat";
  }

  function shouldHandleContact(contact) {
    return isVoiceCall(contact) && isJabraDevice;
  }

  function isEnded(contact) {
    const state = contact.getState();
    return state.type === connect.ContactStateType.ENDED;
  }

  connect.contact(function (contact) {
    if (!shouldHandleContact(contact)) {
      return;
    }

    logger.debug("Setting up new contact", contact);
    contact.onEnded(function (contact) {
      if (!shouldHandleContact(contact)) {
        return;
      }

      const activeContact = getActiveVoiceContact();
      // onEnded is called twice: 1) after call has ended. 2) when after work has ended. We should only hang up after 1).
      if (contact.contactId !== activeContact?.contactId && isEnded(contact)) {
        logger.debug("Amazon Connect contact already ended.");
        return;
      }

      logger.debug("Amazon Connect contact ended.");
      jabra.onHook();
      if (callState.onHold) {
        jabra.resume();
      }

      setIdleState();
    });

    contact.onConnecting(function (contact) {
      if (!shouldHandleContact(contact)) {
        return;
      }

      logger.debug("Amazon Connect contact connecting.");
      if (activeAmazonAgent.getStatus().name === amazonConnectAgentStates.CALLING_CUSTOMER) {
        spc.fromSPConnected();
        logger.debug("Outgoing call connecting.");
      } else {
        spc.fromSPConnecting();
        logger.debug("Incoming call connecting.");
      }
    });

    contact.onConnected(function (contact) {
      if (!shouldHandleContact(contact)) {
        return;
      }

      logger.debug("Amazon Connect contact connected.");
      spc.fromSPConnected();
    });

    /**
     * onRefresh triggers when something with the Amazon Connect contact changes.
     * Update the Jabra headset depending on what state the initial connection + third party connection is
     */
    contact.onRefresh(async function (contact) {
      if (!shouldHandleContact(contact)) {
        return;
      }

      logger.debug("Amazon Connect contact refreshed.");
      let initialConnection = contact.getInitialConnection();
      let thirdPartyConnection = contact.getSingleActiveThirdPartyConnection();

      let state = activeAmazonAgent.getState().name;

      // Only sync state when busy, because this is when the relevant state can change.
      if (state !== amazonConnectAgentStates.BUSY) {
        await stateButtonController.alignStateButtonWithAgentState(activeAmazonAgent.getState().name);
        return;
      }

      let isOnHold = (initialConnection?.isOnHold() ?? false) || (thirdPartyConnection?.isOnHold() ?? false);
      let isOffHook = (initialConnection?.isConnected() ?? false) || (thirdPartyConnection?.isConnected() ?? false);

      setHoldState(isOnHold);
      setHookState(isOffHook);
    });
  });

  /**
   * Set Amazon connect agent functions
   * If user clicks with amazon UI
   */
  connect.agent(function (agent) {
    activeAmazonAgent = agent;

    agent.onMuteToggle(function (mu) {
      if (!isJabraDevice) {
        return;
      }

      logger.debug(`Amazon Connect toggled mute to ${mu.muted}`);
      setMuteState(mu.muted);
    });

    /**
     * Changed state, update headset after changed state
     */
    agent.onStateChange(function (agentState) {
      logger.debug(`Amazon Connect changed state: ${agentState?.oldState} => ${agentState?.newState}`, agentState);
      stateButtonController.alignStateButtonWithAgentState(agentState.newState);
    });
  });

  /**
   * Purpose of this class is to make the Amazon UI integrate with the headsets
   * Ex: Users use headset controller to do stuff
   */
  class PhoneManager {
    fromHeadsetMute() {      
      activeAmazonAgent.mute();
    }

    fromHeadsetUnmute() {      
      activeAmazonAgent.unmute();
    }

    // Toggles hold in Amazon Connect. Wait for refresh to update internal state
    toggleHold(connection) {
      if (!connection) {
        logger.debug("Connection is not active");
        return;
      }

      logger.debug("One active connection. Swapping");
      if (connection.isOnHold()) {
        connection.resume();
      } else {
        connection.hold();
      }
    }

    /**
     * Flash = user long press on answer-btn
     * Puts the call on hold
     */
    fromHeadsetFlash() {
      logger.debug("** flash-function **");
      const activeAmazonContact = getActiveVoiceContact();
      if (!activeAmazonContact) {
        logger.warn("No active Amazon Connect contact");
      }

      try {
        let initialConnection = activeAmazonContact.getActiveInitialConnection();
        let thirdPartyConnection = activeAmazonContact.getSingleActiveThirdPartyConnection();

        if (initialConnection === null || thirdPartyConnection === null) {
          this.toggleHold(initialConnection ?? thirdPartyConnection);
        } else {
          activeAmazonContact.toggleActiveConnections();
        }
      } catch (e) {
        logger.error("An error occurred when swapping or putting the call on hold. Error message: ", e);
      }
    }

    fromHeadsetAcceptCall() {
      const activeAmazonContact = getActiveVoiceContact();
      if (jabraHeadsetState === headsetStates.RING && activeAmazonContact) {
        activeAmazonContact.accept();
        spc.fromSPConnected();
        logger.debug("Accepted call.");
      }
    }

    fromHeadsetRejectCall() {
      const activeAmazonContact = getActiveVoiceContact();
      if (jabraHeadsetState === headsetStates.RING && activeAmazonContact) {
        jabra.unring();
        jabraHeadsetState = headsetStates.UNRING;
        stateButtonController.alignStateButtonWithAgentState(activeAmazonAgent.getState().name);
        logger.debug("Rejected call.");
      }
    }

    /**
     * Depending on which connection is active, destroy it
     * And tell amazon(spc) that its destroyed
     */
    fromHeadsetCloseCall() {
      logger.debug("Getting active contact");
      const activeAmazonContact = getActiveVoiceContact();
      if (!activeAmazonContact) {
        logger.warn("No active Amazon Connect contact");
      }

      logger.debug("Getting initial connection");
      let initialConnection = activeAmazonContact.getActiveInitialConnection();

      logger.debug("Getting 3rd party connection");
      let thirdPartyConnection = activeAmazonContact.getSingleActiveThirdPartyConnection();
      // Close the one that the agent is talking to(the connected)
      if (thirdPartyConnection && thirdPartyConnection.isConnected()) {
        logger.debug("Closing third party connection.");
        thirdPartyConnection.destroy();        
      }
      // initialConnection.isConnecting() = is ringing
      else if (initialConnection && (initialConnection.isConnected() || initialConnection.isConnecting())) {
        logger.debug("Closing initial connection.");
        initialConnection.destroy();        
      }

      logger.debug("Check agent state in closeCall(), state: ", activeAmazonAgent.getState().name);
      stateButtonController.alignStateButtonWithAgentState(activeAmazonAgent.getState().name);
    }

    /**
     * Set new state of the state button
     * depending on the old state
     * Ex:
     * old state: ROUTABLE -> newState: OFFLINE
     * old state: OFFLINE -> newState: ROUTABLE
     * else -> newStat: ROUTABLE
     */
    fromHeadsetSwitchAgentState() {
      let oldStateType = activeAmazonAgent.getState().type;
      logger.debug(`Headset requested state change. Current state type: ${oldStateType}`);
      let newState;
      // If Routable -> set to Offline
      if (oldStateType === connect.AgentStateType.ROUTABLE) {
        newState = this.findAmazonConnectState(connect.AgentStateType.OFFLINE);
      }
      // Offline -> Routable
      else if (oldStateType === connect.AgentStateType.OFFLINE) {
        newState = this.findAmazonConnectState(connect.AgentStateType.ROUTABLE);
      }
      // Someone is calling -> Do nothing
      else if (
        activeAmazonAgent.getState().name === amazonConnectAgentStates.PENDING_BUSY ||
        activeAmazonAgent.getState().name === amazonConnectAgentStates.BUSY
      ) {
        return;
      }
      // old state is "system"(after-call-work), "error", "ended" or something else -> Routable
      else {
        newState = this.findAmazonConnectState(connect.AgentStateType.ROUTABLE);
      }

      // Set agent to the new state
      const currentState = activeAmazonAgent.getState();
      logger.debug(`Update agent state from ${currentState.name} to ${newState.name}`);
      activeAmazonAgent.setState(newState, {
        success: () => {},
        failure: (jsonErr) => {
          logger.error("Failed setting state: " + JSON.stringify(jsonErr));
        },
      });
    }

    /**
     * @private
     * @param {connect.AgentState} connectAgentState
     */
    findAmazonConnectState(connectAgentState) {
      return activeAmazonAgent.getAgentStates().find((state) => {
        return state.type === connectAgentState;
      });
    }
  }

  let pm = new PhoneManager();

  /******************************************
   * Listens for events on the jabra device *
   * and sends it to the Amazon UI          *
   ******************************************/

  function subscribeTo(eventName, callback) {
    jabra.addEventListener(eventName, function() {
      logger.debug(`Device sent <${eventName}> event`);
      try {
        return callback.apply(null, arguments);
      }
      catch(error) {
        logger.error(`Failed reacting to ${eventName} event`, error);
      }
    }); 
  }

  subscribeTo("device attached", async (event) => {
    logger.debug(`New device attached: ${event.data.deviceName}`);
    let newDeviceAttached = event.data;
    if (deviceValidator.isValidDevice(newDeviceAttached)) {
      stateButtonController.reset();
      await setupDevice(newDeviceAttached);
    }
  });

  subscribeTo("device detached", async () => {
    if (jabra) {
      await setupDevice();
    }
  });

  subscribeTo("mute", () => pm.fromHeadsetMute());
  subscribeTo("unmute", () => pm.fromHeadsetUnmute());

  subscribeTo("acceptcall", () => pm.fromHeadsetAcceptCall());

  subscribeTo("flash", () => pm.fromHeadsetFlash());

  subscribeTo("endcall", () => pm.fromHeadsetCloseCall());

  subscribeTo("reject", () => {
    pm.fromHeadsetRejectCall();
    pm.fromHeadsetCloseCall();
  });

  subscribeTo("mmi", async (event) => {    
    if (await stateButtonController.isStateButtonPress(event)) {
      pm.fromHeadsetSwitchAgentState();
    }
  });
}

jabraRCC.setupMargin = 2000;

export default jabraRCC;
